import { isBrowser } from 'utils/helpers';

import { StoreApi as api } from './instances';

export type QuestOrderRequestBody = {
  order: {
    number: string;
    purchase_type: 'psc_one_time' | 'psc_membership' | 'unknown';
    payment_token: string;
    address: {
      address1: string;
      address2: string;
      city: string;
      state: string;
      zipcode: string;
    };
    // user is passed in if they are a new user
    user?: {
      email: string;
      first_name: string;
      last_name: string;
      terms_and_conditions: boolean;
      password: string;
    };
    demographics: {
      dob: string; // format: YYYY-MM-DD
      gender: 'female' | 'male' | null;
      phone_number: string; // format: 555-555-5555
    };
    questionnaire?: {
      medical?: {
        pregnancy_status: 'yes' | 'no';
        hysterectomy_status?:
          | 'yes_removal_both_ovaries'
          | 'yes_removal_one_ovary'
          | 'yes_no_removal'
          | 'no';
        pregnancy_weeks?: number;
        has_had_menstrual_period_over_12_months?:
          | 'yes'
          | 'no_regular_periods'
          | 'no_periods_over_12_months';
        last_menstrual_period_date?: string;
        menstrual_cycle_duration_days?: number;
      };
    };
    marketing: {
      email: boolean;
    };
  };
};

export const placeQuestOrder = async (data: QuestOrderRequestBody) => {
  const { order } = data;

  const formData = new FormData();

  formData.append('_method', 'put');

  formData.append('order[purchase_type]', order.purchase_type);
  formData.append('order[number]', order.number);
  formData.append('order[payment_token]', order.payment_token);

  const {
    demographics: { dob, gender, phone_number },
  } = order;

  formData.append('order[demographics][dob]', dob);
  formData.append('order[demographics][gender]', String(gender));
  formData.append('order[demographics][phone_number]', phone_number);

  const {
    address: { address1, address2, city, state, zipcode },
  } = order;

  formData.append('order[address][address1]', address1);
  formData.append('order[address][address2]', address2);
  formData.append('order[address][city]', city);
  formData.append('order[address][state]', state);
  formData.append('order[address][zipcode]', zipcode);

  const { user } = order;

  if (user) {
    formData.append('order[user][email]', user.email);
    formData.append('order[user][first_name]', user.first_name);
    formData.append('order[user][last_name]', user.last_name);
    formData.append('order[user][password]', user.password);
    formData.append(
      'order[user][terms_and_conditions]',
      String(user.terms_and_conditions),
    );
  }

  const { questionnaire } = order;

  const medical = questionnaire?.medical;

  if (medical) {
    medical.pregnancy_status &&
      formData.append(
        'order[questionnaire][medical][pregnancy_status]',
        medical.pregnancy_status,
      );
    medical.pregnancy_weeks &&
      formData.append(
        'order[questionnaire][medical][pregnancy_weeks]',
        String(medical.pregnancy_weeks),
      );
    medical.hysterectomy_status &&
      formData.append(
        'order[questionnaire][medical][hysterectomy_status]',
        medical.hysterectomy_status,
      );
    medical.has_had_menstrual_period_over_12_months &&
      formData.append(
        'order[questionnaire][medical][has_had_menstrual_period_over_12_months]',
        medical.has_had_menstrual_period_over_12_months,
      );
    medical.last_menstrual_period_date &&
      formData.append(
        'order[questionnaire][medical][last_menstrual_period_date]',
        medical.last_menstrual_period_date,
      );
    medical.menstrual_cycle_duration_days &&
      formData.append(
        'order[questionnaire][medical][menstrual_cycle_duration_days]',
        String(medical.menstrual_cycle_duration_days),
      );
  }

  formData.append(
    'order[marketing][email]',
    String(order.marketing.email ?? true),
  );

  if (isBrowser() && window.freshpaint) {
    formData.append(
      'order[tracker][distinct_id]',
      window.freshpaint.cookie?.props?.distinct_id ?? '',
    );
    formData.append(
      'order[tracker][$device_id]',
      window.freshpaint.cookie?.props?.$device_id ?? '',
    );
    formData.append(
      'order[tracker][$session_id]',
      window.freshpaint.cookie?.props?.$session_id ?? '',
    );
    formData.append(
      'order[tracker][token]',
      window.freshpaint.config?.token ?? '',
    );
    formData.append('order[tracker][$current_url]', window.location.href);
    formData.append('order[tracker][$title]', document.title);
    formData.append('order[tracker][$user_agent]', navigator.userAgent);
    Object.entries(window.freshpaint._consent_elections ?? {}).forEach(
      ([key, value]) => {
        if (typeof value !== 'object') {
          formData.append(`order[tracker][$consent][${key}]`, String(value));
        }
      },
    );
  }

  return api.post('/v3/lab_orders/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...(user && {
      withCredentials: true,
      transformRequest: (data, headers) => {
        delete headers.Authorization;

        return data;
      },
    }),
  });
};

export type QuestLocation = {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  hours: string;
  distance: number;
};

export type PatientServiceCenters = {
  patient_service_centers: QuestLocation[];
};

export const getQuestLocations = async (zipCode: string) => {
  const questLocationsEndpoint = `/v3/patient_service_centers/${zipCode}`;

  const questLocations = await api.get<PatientServiceCenters>(
    questLocationsEndpoint,
  );
  return questLocations.data;
};
